import * as Yup from "yup";
import moment from "moment";
import { types, BadgeColorSchemeType } from "@vilocnv/allsetra-core";
import { TimeLineItemProps } from "components/ui/LocationTimeline/LocationTimeline";
import {
  IObjectRideSegment,
  IRideBulkUpdateKeys,
  IRideBulkUpdateMode,
  IRideBulkUpdateDriver,
  IRideBulkUpdateDriverOrKey
} from "../types";
import { DateTime } from "luxon";

export const isOngoingRide = (
  ride:
    | (types.IObjectRide & { id?: number; status?: number })
    | { id?: number; status?: number }
    | null
    | undefined
): boolean => {
  const r = ride as { id?: number; status?: number } | null | undefined;
  return r?.id === 0 || r?.status === 1;
};

export enum ObjectRideModeEnum {
  BUSINESS = 1,
  PRIVATE = 2,
  COMMUTE = 3,
  MIXED = 4
}

export enum ObjectRideOriginEnum {
  NONE = 0,
  CALCULATED = 1,
  REPROCESSED = 2,
  LEGACY = 3,
  MANUAL = 4
}

export type IObjectRideWithOrigin = types.IObjectRide & {
  origin?: number;
  isMigrated?: boolean;
};

export type IObjectRideWithSegments = IObjectRideWithOrigin & {
  segments?: IObjectRideSegment[];
};

export type RideMapPoint = {
  latitude?: number | null;
  longitude?: number | null;
  uniqueId?: string;
  date?: string;
  speed?: number;
};

export const isManualRide = (ride?: unknown) =>
  (ride as { origin?: number } | null | undefined)?.origin ===
  ObjectRideOriginEnum.MANUAL;

export const shouldUseRideStartEndOnly = (
  ride?: unknown,
  locations?: unknown[] | null
) =>
  Boolean((ride as { isMigrated?: boolean } | null | undefined)?.isMigrated) ||
  isManualRide(ride) ||
  !locations?.length;

export const getValidRideMapPoints = (points: RideMapPoint[]) =>
  points.filter(
    (item): item is RideMapPoint & { latitude: number; longitude: number } =>
      item.latitude != null && item.longitude != null
  );

export const areRideCoordinatesEqual = (
  a?: { latitude?: number | null; longitude?: number | null },
  b?: { latitude?: number | null; longitude?: number | null }
) =>
  a?.latitude != null &&
  a?.longitude != null &&
  a.latitude === b?.latitude &&
  a.longitude === b?.longitude;

export const OBJECT_RIDE_MODES = (translator: any): Record<number, string> =>
  ({
    1: translator("objectRideModes.1"),
    2: translator("objectRideModes.2"),
    3: translator("objectRideModes.3"),
    4: translator("objectRideModes.4")
  }) as const;

export const OBJECT_RIDE_MODES_OPTIONS = (translator: any) => {
  const objectRideModes = OBJECT_RIDE_MODES(translator);

  return Object.keys(objectRideModes)
    .filter((item: any) => item != 4)
    .map((item: any) => ({
      value: item,
      label: objectRideModes[item]
    }));
};

export const OBJECT_RIDE_MODES_COLOR: Record<number, BadgeColorSchemeType> = {
  1: "purple",
  2: "info",
  3: "success",
  4: "warning"
} as const;

export const transformObjectRideDataForForm = (
  objectRide: types.IObjectRide & {
    driver?: any;
    driverId?: string;
    key?: any;
    privateMileage?: number;
    changeReason?: string;
  }
): any => {
  return {
    uniqueId: objectRide.uniqueId,
    keyId: objectRide.key?.uniqueId || "",
    driverId: objectRide?.driver?.uniqueId || objectRide?.driverId || "",
    rideMode: objectRide.rideMode,
    privateMileage: objectRide.privateMileage ?? undefined,
    changeReason: objectRide.changeReason || "",
    comments: objectRide.comments || "",
    startAddress: objectRide.startAddress || "",
    endAddress: objectRide.endAddress || "",
    startAddressPointOfInterest: undefined,
    endAddressPointOfInterest: undefined,
    correctedEndAddress: objectRide.correctedEndAddress || "",
    correctedEndLatitude: objectRide.correctedEndLatitude || 0,
    correctedEndLongitude: objectRide.correctedEndLongitude || 0,
    correctedStartAddress: objectRide.correctedStartAddress || "",
    correctedStartLatitude: objectRide.correctedStartLatitude || 0,
    correctedStartLongitude: objectRide.correctedStartLongitude || 0
  };
};

export const updateObjectRideFormValidationSchema = (t: any) =>
  Yup.object({
    keyId: Yup.string().label("Driver"),
    rideMode: Yup.string()
      .label("Ride Mode")
      .required(t("validation.rideModeRequired")),
    privateMileage: Yup.number()
      .label("Private Mileage")
      .min(0, t("validation.cannotBeNegative")),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(1, t("validation.fieldRequired"))
      .required(t("validation.fieldRequired"))
  });

export const transformObjectRideLocationsForTimeline = (
  objectRideLocations: Array<types.IObjectRideLocation>,
  objectRide: any
): Array<TimeLineItemProps> => {
  const toLatitude = (value: number | null | undefined) =>
    typeof value === "number" && Number.isFinite(value) ? value - 0.00001 : 0;

  return objectRideLocations.length > 1
    ? objectRideLocations.map((item) => ({
        resolvedAddress: item.address,
        date: item?.date,
        latitude: toLatitude(item?.latitude),
        longitude: item?.longitude ?? 0,
        startAddress:
          objectRide?.startAddress || objectRide?.correctedStartAddress,
        endAddress: objectRide?.endAddress || objectRide?.correctedEndAddress
      }))
    : [
        {
          resolvedAddress: objectRide?.startAddress,
          date: objectRide?.startDate,
          latitude: toLatitude(objectRide?.startLatitude),
          longitude: objectRide?.startLongitude ?? 0,
          startAddress:
            objectRide?.startAddress || objectRide?.correctedStartAddress,
          endAddress: objectRide?.endAddress || objectRide?.correctedEndAddress
        },
        {
          resolvedAddress: objectRide?.endAddress,
          date: objectRide?.endDate,
          latitude: toLatitude(objectRide?.endLatitude),
          longitude: objectRide?.endLongitude ?? 0,
          startAddress:
            objectRide?.startAddress || objectRide?.correctedStartAddress,
          endAddress: objectRide?.endAddress || objectRide?.correctedEndAddress
        }
      ];
};

export const getObjectRideTotalTimeInMinutes = (
  objectRide: types.IObjectRide
) => {
  return moment(objectRide.endDate).diff(objectRide.startDate, "minutes");
};

export const getRidesBulkUpdateKeyInitialValues = (
  rides: string[]
): IRideBulkUpdateKeys => ({
  rides,
  keyId: null,
  changeReason: ""
});

export const getRidesBulkUpdateModeInitialValues = (
  rides: string[]
): IRideBulkUpdateMode => ({
  rides,
  rideMode: null,
  changeReason: ""
});

export const ridesBulkUpdateKeyValidationSchema = (t: any) =>
  Yup.object({
    keyId: Yup.string()
      .label("Driver")
      .required(
        t("keyId.required", {
          ns: "formFieldsTranslation"
        })
      ),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(
        1,
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
      .required(
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
  });

export const ridesBulkUpdateModeValidationSchema = (t: any) =>
  Yup.object({
    rideMode: Yup.number()
      .label("Mode")
      .required(
        t("rideType.required", {
          ns: "formFieldsTranslation"
        })
      ),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(
        1,
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
      .required(
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
  });

export const getRidesBulkUpdateDriverInitialValues = (
  rides: string[]
): IRideBulkUpdateDriver => ({
  rides,
  driverId: null,
  changeReason: ""
});

export const ridesBulkUpdateDriverValidationSchema: Yup.Schema = Yup.object({
  driverId: Yup.string().label("Driver").required()
});

export const getRidesBulkUpdateDriverOrKeyInitialValues = (
  rides: string[]
): IRideBulkUpdateDriverOrKey => ({
  rides,
  driverId: null,
  keyId: null,
  changeReason: ""
});

export const ridesBulkUpdateDriverOrKeyValidationSchema = (t: any) =>
  Yup.object({
    driverId: Yup.string().nullable(),
    keyId: Yup.string().nullable(),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(
        1,
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
      .required(
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
  }).test(
    "one-of-driver-or-key",
    "Either driver or key is required",
    (values: any) => {
      const hasDriver = !!values?.driverId;
      const hasKey = !!values?.keyId;
      return (hasDriver && !hasKey) || (!hasDriver && hasKey);
    }
  );

export const getObjectRideKeyValueData = (locations: any[]) => {
  const data: any = {};
  locations?.forEach(
    (item) =>
      (data[`${DateTime.fromISO(item?.date).toFormat("dd MMM yyyy, HH:mm")} `] =
        `${item?.speed?.toFixed(2)} km/h`)
  );
  return data;
};
